import { type PointerEvent as ReactPointerEvent, useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import OpenSeadragon from 'openseadragon';
import { getVersion, listThreads } from '../../lib/api';
import { Spinner } from '../../components/ui';
import { ProofViewer } from './ProofViewer';

interface Side {
  id: string;
  number: number;
}

/**
 * Before / after comparison: the previous version overlays the current one and
 * is clipped at a draggable divider. Both viewers pan/zoom together (scroll to
 * zoom, drag to pan) so the two revisions stay aligned.
 */
export function CompareView({
  versions,
  initialLeftId,
  initialRightId,
}: {
  versions: Side[];
  initialLeftId: string;
  initialRightId: string;
}) {
  const ordered = [...versions].sort((a, b) => a.number - b.number);
  const [leftId, setLeftId] = useState(initialLeftId);
  const [rightId, setRightId] = useState(initialRightId);
  const left = (ordered.find((v) => v.id === leftId) ?? ordered[0])!;
  const right = (ordered.find((v) => v.id === rightId) ?? ordered[ordered.length - 1])!;
  const [pageIndex, setPageIndex] = useState(0);
  const [reveal, setReveal] = useState(0.5);
  const [pageCount, setPageCount] = useState(1);
  const ref = useRef<HTMLDivElement>(null);
  const dragging = useRef(false);
  // Latest page state + a nav cooldown, read inside the (once-bound) wheel handler.
  const pageIndexRef = useRef(pageIndex);
  pageIndexRef.current = pageIndex;
  const pageCountRef = useRef(pageCount);
  pageCountRef.current = pageCount;
  const lastNav = useRef(0);

  // Keep the two viewers' viewports in lock-step.
  const viewers = useRef<{ left?: OpenSeadragon.Viewer; right?: OpenSeadragon.Viewer }>({});
  const linked = useRef(false);
  const syncing = useRef(false);
  const linkViewers = () => {
    const { left: a, right: b } = viewers.current;
    if (!a || !b || linked.current) return;
    linked.current = true;
    const mirror = (from: OpenSeadragon.Viewer, to: OpenSeadragon.Viewer) => () => {
      if (syncing.current) return;
      syncing.current = true;
      to.viewport.zoomTo(from.viewport.getZoom(true), undefined, true);
      to.viewport.panTo(from.viewport.getCenter(true), true);
      to.viewport.applyConstraints(true);
      syncing.current = false;
    };
    a.addHandler('viewport-change', mirror(a, b));
    b.addHandler('viewport-change', mirror(b, a));
  };
  useEffect(() => () => {
    linked.current = false;
    viewers.current = {};
  }, []);

  // Wheel: Ctrl/⌘+scroll zooms both viewers toward the cursor (snappy synced zoom,
  // since OSD's own scroll-zoom is disabled in compare panes); a plain scroll pages
  // through the document instead, paced by a short cooldown so one flick moves one
  // page rather than racing through the whole proof.
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const onWheel = (e: WheelEvent) => {
      e.preventDefault();
      let dy = e.deltaY;
      if (e.deltaMode === 1) dy *= 16;
      else if (e.deltaMode === 2) dy *= el.clientHeight;

      if (e.ctrlKey || e.metaKey) {
        const { left: a, right: b } = viewers.current;
        if (!a || !b) return;
        const factor = Math.exp(-dy * 0.0025);
        const rect = el.getBoundingClientRect();
        syncing.current = true;
        for (const v of [a, b]) {
          if (!v.isOpen()) continue;
          const px = new OpenSeadragon.Point(e.clientX - rect.left, e.clientY - rect.top);
          v.viewport.zoomBy(factor, v.viewport.pointFromPixel(px, true), true);
          v.viewport.applyConstraints(true);
        }
        syncing.current = false;
        return;
      }

      // Plain scroll → change page (cooldown-paced, direction from scroll sign).
      if (Math.abs(dy) < 2) return;
      const now = Date.now();
      if (now - lastNav.current < 260) return;
      const dir = dy > 0 ? 1 : -1;
      const next = Math.min(pageCountRef.current - 1, Math.max(0, pageIndexRef.current + dir));
      if (next !== pageIndexRef.current) {
        lastNav.current = now;
        setPageIndex(next);
      }
    };
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, []);

  const onHandleDown = (e: ReactPointerEvent) => {
    dragging.current = true;
    // Touch drags must not also scroll/zoom the page underneath, and the move
    // events have to keep coming after the finger leaves the 32px handle.
    e.preventDefault();
    e.currentTarget.setPointerCapture(e.pointerId);
  };
  const onHandleMove = (e: ReactPointerEvent) => {
    if (!dragging.current || !ref.current) return;
    e.preventDefault();
    const rect = ref.current.getBoundingClientRect();
    setReveal(Math.min(1, Math.max(0, (e.clientX - rect.left) / rect.width)));
  };
  const onHandleUp = (e: ReactPointerEvent) => {
    dragging.current = false;
    try {
      e.currentTarget.releasePointerCapture(e.pointerId);
    } catch {
      /* ignore */
    }
  };

  return (
    <div ref={ref} className="relative h-full min-h-0 overflow-hidden bg-neutral-100">
      {/* After (current) underneath, full */}
      <div className="absolute inset-0">
        <ComparePane
          side={right}
          pageIndex={pageIndex}
          onPageChange={setPageIndex}
          onLoaded={setPageCount}
          onViewer={(v) => {
            viewers.current.right = v;
            linkViewers();
          }}
        />
      </div>
      {/* Before (previous) on top, clipped to the left of the divider */}
      <div className="absolute inset-0" style={{ clipPath: `inset(0 ${(1 - reveal) * 100}% 0 0)` }}>
        <ComparePane
          side={left}
          pageIndex={pageIndex}
          onPageChange={setPageIndex}
          onViewer={(v) => {
            viewers.current.left = v;
            linkViewers();
          }}
        />
      </div>

      {/* Draggable divider */}
      <div
        onPointerDown={onHandleDown}
        onPointerMove={onHandleMove}
        onPointerUp={onHandleUp}
        onPointerCancel={onHandleUp}
        // `touch-none` is what makes this draggable on a phone: without it the
        // browser claims the gesture as a scroll/pinch and cancels the pointer.
        // The hit area is a fat 32px on touch, the usual 16px from md up.
        className="absolute inset-y-0 z-30 -ml-4 w-8 touch-none cursor-col-resize md:-ml-2 md:w-4"
        style={{ left: `${reveal * 100}%` }}
      >
        <div className="pointer-events-none absolute inset-y-0 left-1/2 w-0.5 -translate-x-1/2 bg-blue-500" />
        <div className="pointer-events-none absolute left-1/2 top-1/2 flex h-9 w-9 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full bg-blue-500 text-white shadow md:h-7 md:w-7">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} className="h-4 w-4">
            <path d="M9 7l-4 5 4 5M15 7l4 5-4 5" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </div>
      </div>

      {/* Version pickers */}
      <div className="absolute left-3 top-3 z-30 flex max-w-[calc(100%-1.5rem)] flex-wrap items-center gap-x-2 gap-y-1 rounded-md bg-black/70 px-2 py-1 text-xs font-medium text-white">
        <span className="hidden text-white/70 sm:inline">Before</span>
        <select
          value={left.id}
          onChange={(e) => setLeftId(e.target.value)}
          className="rounded bg-white/10 px-1 py-0.5 text-white outline-none"
        >
          {ordered.map((v) => (
            <option key={v.id} value={v.id} className="text-neutral-900">
              v{v.number}
            </option>
          ))}
        </select>
        <span className="text-white/40">vs</span>
        <span className="hidden text-white/70 sm:inline">After</span>
        <select
          value={right.id}
          onChange={(e) => setRightId(e.target.value)}
          className="rounded bg-white/10 px-1 py-0.5 text-white outline-none"
        >
          {ordered.map((v) => (
            <option key={v.id} value={v.id} className="text-neutral-900">
              v{v.number}
            </option>
          ))}
        </select>
      </div>

      {/* Page navigation (multi-page) */}
      {pageCount > 1 && (
        <div className="absolute bottom-3 left-1/2 z-20 flex -translate-x-1/2 items-center gap-2 rounded-full border border-neutral-200 bg-white/95 px-2 py-1 text-xs shadow">
          <button
            type="button"
            onClick={() => setPageIndex((i) => Math.max(0, i - 1))}
            disabled={pageIndex === 0}
            title="Previous page"
            aria-label="Previous page"
            className="px-1 text-neutral-600 disabled:opacity-30"
          >
            ‹
          </button>
          <span className="text-neutral-500">
            Page {pageIndex + 1} / {pageCount}
          </span>
          <button
            type="button"
            onClick={() => setPageIndex((i) => Math.min(pageCount - 1, i + 1))}
            disabled={pageIndex >= pageCount - 1}
            title="Next page"
            aria-label="Next page"
            className="px-1 text-neutral-600 disabled:opacity-30"
          >
            ›
          </button>
        </div>
      )}

      {/* Controls hint */}
      <span className="pointer-events-none absolute bottom-3 right-3 z-20 hidden rounded bg-black/55 px-2 py-0.5 text-[11px] text-white md:block">
        Scroll to change pages · Ctrl+scroll to zoom
      </span>
    </div>
  );
}

function ComparePane({
  side,
  pageIndex,
  onPageChange,
  onLoaded,
  onViewer,
}: {
  side: Side;
  pageIndex: number;
  onPageChange: (i: number) => void;
  onLoaded?: (pageCount: number) => void;
  onViewer: (viewer: OpenSeadragon.Viewer) => void;
}) {
  const version = useQuery({
    queryKey: ['version', side.id],
    queryFn: () => getVersion(side.id),
    refetchInterval: (q) => {
      const s = q.state.data?.status;
      return s === 'READY' || s === 'FAILED' ? false : 1500;
    },
  });
  const v = version.data;
  const pageCount = v?.status === 'READY' ? v.pages.length : 0;
  useEffect(() => {
    if (pageCount > 0) onLoaded?.(pageCount);
  }, [pageCount, onLoaded]);

  // This side's own comments — shown read-only so both revisions' feedback is
  // visible while comparing (the reveal slider clips each to its side).
  const threads = useQuery({
    queryKey: ['threads', side.id],
    queryFn: () => listThreads(side.id),
    enabled: v?.status === 'READY',
  });

  if (!v || v.status !== 'READY' || v.pages.length === 0) {
    return (
      <div className="flex h-full items-center justify-center">
        <Spinner />
      </div>
    );
  }
  return (
    <ProofViewer
      version={v}
      pageIndex={Math.min(pageIndex, v.pages.length - 1)}
      onPageChange={onPageChange}
      threads={threads.data ?? []}
      tool={null}
      pending={null}
      onPlaceAnchor={() => {}}
      selectedThreadId={null}
      onSelectThread={() => {}}
      onSelectTool={() => {}}
      onViewer={onViewer}
      interactive={false}
    />
  );
}
